import { useMutation } from "@apollo/client";
import { UPDATE_EVENT_MUTATION } from "@/pages/events/[eventSlug]/queries";
import { useSnackbar } from "notistack";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";

interface UseRecruitmentPhotoDeletionProps {
  eventId: string;
  currentPhotoUploads: string[];
  recruitmentData: any;
  onPhotoDeleted: (newPhotoUploads: string[]) => void;
}

export function useRecruitmentPhotoDeletion({
  eventId,
  currentPhotoUploads,
  recruitmentData,
  onPhotoDeleted
}: UseRecruitmentPhotoDeletionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { setError } = useErrorBanner();

  const [updateEventMutation] = useMutation(UPDATE_EVENT_MUTATION);

  const deletePhoto = async (photoId: string) => {
    try {
      // Remove the photo URL from the event's photoUploads array
      // We need to match the photoId with the actual URL in the array
      const updatedPhotoUploads = currentPhotoUploads.filter((_url, index) => {
        // Since photoId is in format "photo-{index}", we can use the index
        // to match it with the URL in the array
        const photoIndex = parseInt(photoId.replace("photo-", ""));
        return index !== photoIndex;
      });

      // Update the event with the new photoUploads array while preserving all other data
      const { data: responseData } = await updateEventMutation({
        variables: {
          input: {
            id: eventId,
            recruitmentEventAttributes: {
              isFinalized: recruitmentData?.isFinalized,
              photoUploads: updatedPhotoUploads
            }
          }
        }
      });

      const { success } = responseData.updateEvent;
      if (success) {
        onPhotoDeleted(updatedPhotoUploads);
        enqueueSnackbar("Photo deleted successfully");
      } else {
        throw new Error(responseData.updateEvent.message || "Failed to update event");
      }
    } catch (error) {
      console.error("Error deleting photo:", error);
      setError("Failed to delete photo. Please try again.");
      throw error;
    }
  };

  return { deletePhoto };
}
